/**
 * TemperatureOcrField — Snap-to-fill temperature input.
 *
 * Pilot: Cooking Record only. Renders next to the manual °C input; when
 * the user opts to snap a thermometer photo we POST to
 * `/api/v1/ocr/temperature` (Gemini 3 Flash Preview), then show a
 * verify-before-submit banner. The parent decides what happens on
 * confirm (usually: set the temp_c form field + stash the OCR trail).
 *
 * States: idle → capturing → detected-verify → failed-retry → manual.
 *
 * After 2 consecutive failed OCR calls we auto-switch to plain manual
 * entry to avoid trapping staff in a retake loop.
 */
import { useRef, useState } from "react";
import { Camera, Upload, CheckCircle2, RefreshCw, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_FAILS = 2;

function tierBadge(tier) {
  const map = {
    high:   { bg: "#dcfce7", fg: "#166534", label: "High confidence" },
    medium: { bg: "#fef9c3", fg: "#854d0e", label: "Medium confidence" },
    low:    { bg: "#fee2e2", fg: "#991b1b", label: "Low confidence — please verify" },
  };
  const m = map[tier] || map.low;
  return (
    <span data-testid="ocr-tier-badge"
      style={{ background: m.bg, color: m.fg, padding: "2px 8px",
               borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
      {m.label}
    </span>
  );
}

/**
 * @param {{
 *   context?: string,                       // "cooking" (default)
 *   minC?: number | null,                   // Optional equipment operating range (°C).
 *   maxC?: number | null,                   //   When supplied the backend uses these
 *                                           //   for the range check instead of the
 *                                           //   cooking-record fallback.
 *   disabled?: boolean,
 *   onConfirm: (r: {temp_c: number, ocr_photo_path: string,
 *                    ocr_parsed_value: number, ocr_confidence: number}) => void,
 *   onFallbackToManual?: () => void,        // called after MAX_FAILS
 * }} props
 */
export default function TemperatureOcrField({
  context = "cooking", minC, maxC, disabled = false, onConfirm, onFallbackToManual,
}) {
  const { tenant } = useAuth();
  // Tenant business setting decides how ambiguous readings (a bare "72"
  // with no °C/°F glyph) are interpreted — so we never interrupt the
  // operator with a unit picker mid-service. Defaults to Celsius.
  const tenantUnit = (tenant?.operational?.temperature_unit || "C").toUpperCase();
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState("idle"); // idle | verify | failed | manual
  const [result, setResult] = useState(null); // OCR response payload
  const [fails, setFails] = useState(0);
  // Two hidden inputs — the camera input carries `capture="environment"`
  // so mobile browsers open the rear camera directly; the upload input
  // omits `capture` so the OS gallery / file picker opens instead.
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  // Remember which trigger the operator last used so the failed-card's
  // "Retake" and the verify-card's "Retake" re-open the same source.
  const lastTriggerRef = useRef("camera");

  const openCamera = () => {
    if (disabled || busy) return;
    lastTriggerRef.current = "camera";
    cameraInputRef.current?.click();
  };
  const openUpload = () => {
    if (disabled || busy) return;
    lastTriggerRef.current = "upload";
    uploadInputRef.current?.click();
  };
  // Used by Retake buttons — re-fires whichever input was last used.
  const reopenLast = () => {
    if (lastTriggerRef.current === "upload") openUpload();
    else openCamera();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same photo later
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please pick a JPEG, PNG or WEBP photo.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("context", context);
      // Caller-supplied operating range in °C — the server uses this to
      // set `in_expected_range` accurately per equipment. Skipped when
      // undefined so the cooking-record pilot continues to use its own
      // fallback lookup.
      if (typeof minC === "number" && Number.isFinite(minC)) {
        fd.append("min_c", String(minC));
      }
      if (typeof maxC === "number" && Number.isFinite(maxC)) {
        fd.append("max_c", String(maxC));
      }
      const { data } = await api.post("/ocr/temperature", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Ambiguous unit — reading is clear but no °C/°F glyph on the
      // display. Auto-resolve using the tenant's business setting
      // (Settings → Operational → Temperature unit) so the operator
      // isn't interrupted with a picker mid-service.
      if (data.unit_ambiguous) {
        const celsius = tenantUnit === "F"
          ? data.temperature_celsius_if_f
          : data.temperature_celsius_if_c;
        setResult({
          ...data,
          unit: tenantUnit,
          temperature_celsius: celsius,
          unit_ambiguous: false,
        });
        setState("verify");
        return;
      }
      // Any retry_reason or missing celsius → treat as failure.
      if (data.retry_reason || data.temperature_celsius == null) {
        const nextFails = fails + 1;
        setFails(nextFails);
        setResult(data);
        if (nextFails >= MAX_FAILS) {
          setState("manual");
          onFallbackToManual?.();
        } else {
          setState("failed");
        }
        return;
      }
      setResult(data);
      setState("verify");
    } catch (err) {
      const nextFails = fails + 1;
      setFails(nextFails);
      toast.error(formatErr(err.response?.data?.detail) || "OCR read failed — please retake or enter manually.");
      if (nextFails >= MAX_FAILS) {
        setState("manual");
        onFallbackToManual?.();
      } else {
        setState("failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmReading = () => {
    if (!result) return;
    onConfirm?.({
      temp_c: Number(result.temperature_celsius),
      ocr_photo_path: result.image_path,
      ocr_parsed_value: Number(result.temperature_celsius),
      ocr_confidence: Number(result.confidence),
    });
    // Reset so the operator can re-snap for a fresh reading if needed.
    setState("idle");
    setResult(null);
    setFails(0);
  };

  // Note: ambiguous-unit readings are auto-resolved via the tenant's
  // business setting (Settings → Operational → Temperature unit) inside
  // handleFile, so we never surface a unit picker mid-service.

  const cancel = () => {
    setState("idle");
    setResult(null);
  };

  // Fallback state — parent has been told to show manual entry. Give
  // staff a subtle button to try OCR again if they want.
  if (state === "manual") {
    return (
      <div className="text-xs opacity-70 flex items-center gap-2" data-testid="ocr-manual-fallback">
        <AlertTriangle size={14} />
        <span>OCR unavailable — please enter the temperature manually.</span>
        <button type="button" className="underline"
          data-testid="ocr-manual-retry"
          onClick={() => { setFails(0); setState("idle"); }}>
          Try OCR again
        </button>
      </div>
    );
  }

  return (
    <div className="grid gap-2" data-testid="ocr-field">
      {/* Two hidden inputs — the camera input has `capture="environment"`
          so tapping it on a phone opens the rear camera; the upload
          input omits `capture` so it opens the gallery / file picker. */}
      <input ref={cameraInputRef} type="file" accept="image/jpeg,image/png,image/webp"
        capture="environment" hidden onChange={handleFile}
        data-testid="ocr-camera-input" />
      <input ref={uploadInputRef} type="file" accept="image/jpeg,image/png,image/webp"
        hidden onChange={handleFile}
        data-testid="ocr-upload-input" />

      {state === "idle" && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button"
            className="vb-btn ghost text-xs inline-flex items-center justify-center gap-1"
            disabled={disabled || busy}
            onClick={openCamera}
            data-testid="ocr-snap-btn">
            <Camera size={14} />
            {busy ? "Reading photo…" : "Take photo"}
          </button>
          <button type="button"
            className="vb-btn ghost text-xs inline-flex items-center justify-center gap-1"
            disabled={disabled || busy}
            onClick={openUpload}
            data-testid="ocr-upload-btn">
            <Upload size={14} />
            Upload photo
          </button>
        </div>
      )}

      {state === "verify" && result && (
        <div className="vb-card p-3 grid gap-2"
          style={{ borderColor: "#93c5fd", background: "#eff6ff" }}
          data-testid="ocr-verify-card">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} style={{ color: "#1d4ed8" }} />
            <strong>Detected reading</strong>
            {tierBadge(result.confidence_tier)}
          </div>
          <div className="text-lg font-semibold" data-testid="ocr-detected-value">
            {result.temperature_celsius}°C
            {result.unit === "F" && result.temperature != null && (
              <span className="text-xs opacity-60 ml-2">
                (from {result.temperature}°F)
              </span>
            )}
          </div>
          {result.notes && (
            <div className="text-xs opacity-75" data-testid="ocr-notes">{result.notes}</div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="button" className="vb-btn w-full sm:w-auto"
              onClick={confirmReading}
              data-testid="ocr-confirm-btn">
              Use this reading
            </button>
            <button type="button" className="vb-btn ghost w-full sm:w-auto inline-flex items-center justify-center gap-1"
              onClick={reopenLast}
              data-testid="ocr-retake-btn">
              <RefreshCw size={14} /> Retake
            </button>
            <button type="button" className="vb-btn ghost w-full sm:w-auto inline-flex items-center justify-center gap-1"
              onClick={cancel}
              data-testid="ocr-cancel-btn">
              <X size={14} /> Enter manually
            </button>
          </div>
        </div>
      )}

      {state === "failed" && (
        <div className="vb-card p-3 grid gap-2"
          style={{ borderColor: "#fca5a5", background: "#fef2f2" }}
          data-testid="ocr-failed-card">
          <div className="flex items-center gap-2" style={{ color: "#b91c1c" }}>
            <AlertTriangle size={16} />
            <strong>
              {result?.retry_reason === "no_display" && "No thermometer display detected"}
              {result?.retry_reason === "unreadable" && "Couldn’t read the display clearly"}
              {result?.retry_reason === "low_confidence" && "Reading too uncertain"}
              {!result?.retry_reason && "OCR failed"}
            </strong>
          </div>
          <div className="text-xs opacity-80">
            {result?.notes || "Please retake the photo — hold steady, avoid glare, and centre the display."}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="button" className="vb-btn w-full sm:w-auto inline-flex items-center justify-center gap-1"
              onClick={reopenLast}
              data-testid="ocr-retry-btn">
              <RefreshCw size={14} /> Retake photo
            </button>
            <button type="button" className="vb-btn ghost w-full sm:w-auto"
              onClick={() => { setFails(0); cancel(); }}
              data-testid="ocr-give-up-btn">
              Enter manually
            </button>
          </div>
          <div className="text-xs opacity-60">
            Attempt {fails} of {MAX_FAILS}. After {MAX_FAILS} we’ll switch to manual entry.
          </div>
        </div>
      )}
    </div>
  );
}
